/**
 * Audio Settings Configuration for Mythbound Adventure Studio™ Voice Bridge
 * 
 * These constants define the audio parameters used across the platform for
 * voice communication between Discord and web users. All audio processing
 * must use these exact specifications for compatibility.
 * 
 * Standards:
 * - Sample Rate: 48kHz (Discord standard)
 * - Channels: Mono (1 channel for processing efficiency)
 * - Bit Depth: 16-bit PCM (quality vs bandwidth balance)
 * - Frame Size: 4096 samples (optimal for real-time processing)
 * 
 * Updated v1.1.0: Added jitter buffer preset configuration per FRONTEND_TODO.MD
 * 
 * @fileoverview Voice Bridge audio configuration constants
 * @version 1.1.0
 * @since v0.0.06
 */

/**
 * Core audio processing constants for voice bridge
 * 
 * CRITICAL: These values must match exactly with backend audio processing
 * Any changes require synchronized updates across all repositories
 */
export const VOICE_BRIDGE_CONFIG = {
  /** Audio sample rate in Hz - Discord standard */
  SAMPLE_RATE: 48000,
  
  /** Number of audio channels - Mono for processing efficiency */
  CHANNELS: 1,
  
  /** Audio bit depth for PCM encoding */
  BIT_DEPTH: 16,
  
  /** Frame size for real-time audio processing */
  FRAME_SIZE: 4096,
  
  /** Maximum users per campaign voice session */
  MAX_SESSION_USERS: 6,
  
  /** Audio format for WebRTC media constraints */
  MEDIA_CONSTRAINTS: {
    audio: {
      sampleRate: 48000,
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      latency: 0.01 // 10ms target latency
    },
    video: false
  },
  
  /** Voice activity detection thresholds */
  VOICE_DETECTION: {
    /** Minimum RMS amplitude to consider as speech */
    SPEECH_THRESHOLD: 0.02,
    
    /** Silence timeout before ending speech detection (ms) */
    SILENCE_TIMEOUT: 2000,
    
    /** Minimum speech duration to process (ms) */
    MIN_SPEECH_DURATION: 500
  },
  
  /** Audio quality presets for different connection types */
  QUALITY_PRESETS: {
    HIGH: {
      sampleRate: 48000,
      bitrate: 128000
    },
    MEDIUM: {
      sampleRate: 44100, 
      bitrate: 96000
    },
    LOW: {
      sampleRate: 22050,
      bitrate: 64000
    }
  },

  /**
   * Jitter buffer presets for audio playback
   * Per FRONTEND_TODO.MD: "Responsive" vs "Smooth" mapped to backend-configured defaults
   * 
   * These presets control the tradeoff between latency and playback smoothness:
   * - Responsive: Low latency, may have glitches on unstable connections
   * - Balanced: Default, good for most network conditions
   * - Smooth: Higher latency, more resilient to network jitter
   */
  JITTER_BUFFER_PRESETS: {
    RESPONSIVE: {
      id: 'responsive' as const,
      bufferMs: 50,
      minBufferMs: 25,
      maxBufferMs: 100,
      label: 'Responsive',
      description: 'Low latency (50ms) - Best for fast, stable connections'
    },
    BALANCED: {
      id: 'balanced' as const,
      bufferMs: 100,
      minBufferMs: 50,
      maxBufferMs: 200,
      label: 'Balanced',
      description: 'Balanced (100ms) - Good for most connections'
    },
    SMOOTH: {
      id: 'smooth' as const,
      bufferMs: 150,
      minBufferMs: 100,
      maxBufferMs: 300,
      label: 'Smooth',
      description: 'Smooth (150ms) - Best for unstable connections'
    }
  },

  /** Default jitter buffer preset */
  DEFAULT_BUFFER_PRESET: 'balanced' as const
} as const;

/**
 * WebSocket audio message types for voice communication
 * These must match the backend WebSocket handler expectations
 */
export const VOICE_MESSAGE_TYPES = {
  /** Join a campaign voice session */
  VOICE_JOIN: 'voice.join',
  
  /** Leave a campaign voice session */
  VOICE_LEAVE: 'voice.leave',
  
  /** Send audio data for transcription */
  VOICE_DATA: 'voice.data',
  
  /** Voice activity status update */
  VOICE_ACTIVITY: 'voice.activity',
  
  /** Voice session status */
  VOICE_STATUS: 'voice.status',
  
  /** Error in voice processing */
  VOICE_ERROR: 'voice.error'
} as const;

/**
 * Character identity formatting for voice sessions
 * Matches backend character identity system
 */
export const CHARACTER_IDENTITY = {
  /** Format for character display names in voice */
  DISPLAY_FORMAT: '{CharacterName}_chr{character_id}',
  
  /** Regex for cleaning character names */
  NAME_CLEAN_REGEX: /[^a-zA-Z0-9]/g,
  
  /** Maximum character name length for voice display */
  MAX_NAME_LENGTH: 20
} as const;

/**
 * Voice session connection states
 */
export const VOICE_CONNECTION_STATE = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting', 
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
} as const;

/**
 * Browser compatibility detection for voice features
 * 
 * @returns Object with browser voice capability flags
 */
export function detectVoiceCapabilities() {
  const capabilities = {
    webRTC: false,
    mediaDevices: false,
    audioContext: false,
    webSocket: false,
    worklets: false
  };
  
  // Check WebRTC support
  capabilities.webRTC = !!(
    navigator.mediaDevices && 
    navigator.mediaDevices.getUserMedia
  );
  
  // Check MediaDevices API
  capabilities.mediaDevices = !!navigator.mediaDevices;
  
  // Check Web Audio API
  capabilities.audioContext = !!(
    window.AudioContext || 
    (window as any).webkitAudioContext
  );
  
  // Check WebSocket support
  capabilities.webSocket = !!window.WebSocket;
  
  // Check AudioWorklet support
  capabilities.worklets = !!(
    capabilities.audioContext && 
    AudioContext.prototype.audioWorklet
  );
  
  return capabilities;
}

/**
 * Validate audio configuration compatibility
 * 
 * @param config - Audio configuration to validate
 * @returns True if configuration is valid for voice bridge
 */
export function validateAudioConfig(config: any): boolean {
  const required = VOICE_BRIDGE_CONFIG;
  
  return (
    config.sampleRate === required.SAMPLE_RATE &&
    config.channelCount === required.CHANNELS
  );
}

/**
 * Get optimal audio settings based on browser capabilities
 * 
 * @returns MediaStreamConstraints optimized for current browser
 */
export function getOptimalAudioSettings(): MediaStreamConstraints {
  const capabilities = detectVoiceCapabilities();
  
  if (!capabilities.webRTC) {
    throw new Error('WebRTC not supported - voice features unavailable');
  }
  
  return {
    audio: {
      ...VOICE_BRIDGE_CONFIG.MEDIA_CONSTRAINTS.audio,
      // Adjust settings based on browser capabilities
      sampleRate: capabilities.audioContext 
        ? VOICE_BRIDGE_CONFIG.SAMPLE_RATE 
        : 44100
    },
    video: false
  };
}

// Type exports for TypeScript
export type VoiceConnectionState = typeof VOICE_CONNECTION_STATE[keyof typeof VOICE_CONNECTION_STATE];
export type VoiceMessageType = typeof VOICE_MESSAGE_TYPES[keyof typeof VOICE_MESSAGE_TYPES]; 
export type VoiceCapabilities = ReturnType<typeof detectVoiceCapabilities>;
export type JitterBufferPresetId = typeof VOICE_BRIDGE_CONFIG.JITTER_BUFFER_PRESETS[keyof typeof VOICE_BRIDGE_CONFIG.JITTER_BUFFER_PRESETS]['id'];

/**
 * WebSocket message types for buffer profile signaling
 * Sent to backend to indicate preferred buffer profile
 */
export const BUFFER_PROFILE_MESSAGE = {
  /** Message type for setting buffer profile preference */
  TYPE: 'voice.buffer_profile',
  
  /** Create buffer profile message payload */
  create: (preset: JitterBufferPresetId): { type: string; data: { preset: JitterBufferPresetId } } => ({
    type: 'voice.buffer_profile',
    data: { preset }
  })
} as const;

/**
 * Get jitter buffer preset configuration by ID
 * 
 * @param presetId - Preset identifier (responsive, balanced, smooth)
 * @returns Preset configuration or balanced as fallback
 */
export function getJitterBufferPreset(presetId: JitterBufferPresetId) {
  const presets = VOICE_BRIDGE_CONFIG.JITTER_BUFFER_PRESETS;
  switch (presetId) {
    case 'responsive':
      return presets.RESPONSIVE;
    case 'smooth':
      return presets.SMOOTH;
    case 'balanced':
    default:
      return presets.BALANCED;
  }
}

/**
 * Get all available jitter buffer presets as an array
 * Useful for rendering UI dropdowns
 * 
 * @returns Array of preset configurations
 */
export function getAllJitterBufferPresets() {
  return [
    VOICE_BRIDGE_CONFIG.JITTER_BUFFER_PRESETS.RESPONSIVE,
    VOICE_BRIDGE_CONFIG.JITTER_BUFFER_PRESETS.BALANCED,
    VOICE_BRIDGE_CONFIG.JITTER_BUFFER_PRESETS.SMOOTH
  ];
}